import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { ChartData } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { WidgetPanelChart } from 'src/app/interfaces/widget-panel-chart';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { ChartWidgetComponent } from 'src/app/shared-components/dashboard/widget/chart-widget/chart-widget.component';

@Component({
  selector: 'app-vendor-trades-widget',
  templateUrl: './vendor-trades-widget.component.html',
  styleUrls: ['./vendor-trades-widget.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorTradesWidgetComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private companyService: CompanyService,
    private ToastService: ToastService,
    private Router: Router,
    private SharedDataService: SharedDataService
  ) {
    this.SharedDataService.listenForEvent<
      string | null | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'chartClicked' && typeof data === 'string') {
          this.navigateToMyVendorGrid(data);
        }
        this._cdr.markForCheck();
      }
    });
  }

  @Input() CompanyId!: number;
  @ViewChild(ChartWidgetComponent) chartComponent!: ViewContainerRef;

  title: string = 'Vendor Trades';
  showMoreTrades: boolean = false;
  widgetPanelCharts: WidgetPanelChart[] = [];
  companyVendorTrades: { key: string; value: number }[] = [];

  formatData() {
    this.widgetPanelCharts = [];
    let data = this.companyVendorTrades;

    if (!this.showMoreTrades) {
      data = data.slice(0, 5);
    }

    const chartData: ChartData = {
      labels: data.map((data: { key: string }) => data.key),
      datasets: [
        {
          data: data.map((data: { value: number }) => data.value)
          // borderWidth: 1,
          // barThickness: 20,
          // maxBarThickness: 20,
          // categoryPercentage: .5
          // barPercentage: 0.6
          // minBarLength: 2
        }
      ]
    };

    this.widgetPanelCharts.push({
      title: this.title,
      data: {
        chartTitle: this.title,
        chartType: 'bar',
        chartData: chartData,
        chartOptions: {
          indexAxis: 'y',
          scales: {
            x: {
              display: false,
              border: {
                display: false
              },
              grid: {
                display: false
              }
            },
            y: {
              display: false,
              border: {
                display: false
              },
              grid: {
                display: false
              }
            }
          },
          layout: {
            padding: {
              bottom: 30,
              top: 0
            }
          },
          plugins: {
            legend: {
              display: false
            }
          }
        },
        chartStyle: {
          outerHeight: '500px'
        },
        displayChartTitle: false,
        chartPlugins: [
          {
            id: 'center-label',
            afterDatasetDraw(chart, args, options) {
              const {
                ctx,
                data,
                chartArea: { left },
                scales: { x, y }
              } = chart;

              ctx.save();

              const useSmallerSize = data.labels!.length > 5;

              data.datasets.forEach((dataset) => {
                dataset.data.forEach((item, index) => {
                  ctx.font = useSmallerSize ? '8px Rubik' : '10px Rubik';
                  ctx.fillStyle = 'rgb(255, 255, 255)';
                  ctx.fillText(
                    String(data.labels![index]),
                    left + 10,
                    y.getPixelForValue(index) + (useSmallerSize ? 3 : 4)
                  );
                });
              });
            }
          }
        ]
      }
    });
  }

  private navigateToMyVendorGrid(tradeCategoryName: string) {
    this.Router.navigateByUrl(`client/${this.CompanyId}/vendors/client`, {
      state: {
        title: tradeCategoryName,
        FilterModel: {
          vendorTradeCategories: {
            filterType: 'text',
            filter: tradeCategoryName,
            type: 'contains'
          }
        }
      }
    });
  }

  showMoreLessTrades() {
    this.showMoreTrades = !this.showMoreTrades;

    this.SharedDataService.sendEvent('loadMoreTrades');
    this.loadVendorRatings(this.CompanyId);
  }

  private loadVendorRatings(companyId: number) {
    this.companyService.getCompanyVendorTrades(companyId).subscribe({
      next: (companyVendorTrades: { key: string; value: number }[]) => {
        if (companyVendorTrades && companyVendorTrades.length > 0) {
          this.companyVendorTrades = companyVendorTrades;
          this.formatData();
        }
        this._cdr.markForCheck();
      },
      error: (err) => {
        throw this.ToastService.addErrorToast('Error', err, {
          returnError: true
        });
      }
    });
  }

  ngOnInit(): void {
    this.loadVendorRatings(this.CompanyId);
  }
}
